import { useState } from "react"
import { toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useTranslationPromoteMutation } from "@/v2-backoffice-client/translation/missing/translation.promote.mutation"
import type { TranslationPromoteMutation } from "@/v2-backoffice-client/translation/missing/__types__/translation.promote.mutation"

type PromoteResult = TranslationPromoteMutation["promoteCodeTexts"][number]

export type TranslationPromoteActionProps = {
  // Row shape of TranslationMissingTable; only the code is needed.
  selectedRows: { code: string }[]
  onComplete?(): void
}

// Selection-toolbar actions for "Missing in database": promote the selected
// code texts into real common.Features rows.
//
//   - Promote: seeds the English text from code; the new features surface in
//     the "Missing translations" queue for the normal editor/AI flow. Fast,
//     one request for the whole selection.
//   - Promote + AI translate: additionally lets the DB-side LLM draft every
//     supported language. One LLM round-trip per code, so the codes are sent
//     one per request with a progress counter.
function TranslationPromoteAction(props: TranslationPromoteActionProps) {
  const { selectedRows, onComplete } = props

  const [promote, promoteState] = useTranslationPromoteMutation()
  const [aiProgress, setAiProgress] = useState<{
    done: number
    total: number
  } | null>(null)

  const codes = selectedRows.map((row) => row.code)
  const busy = promoteState.loading || aiProgress !== null

  function report(results: PromoteResult[]) {
    const failed = results.filter((result) => !result.Ok)
    const promoted = results.length - failed.length
    if (failed.length === 0) {
      toast({
        title: `Promoted ${promoted} text(s) — now editable under "All texts" / "Missing translations".`,
      })
      return
    }
    toast({
      variant: "destructive",
      title: `Promoted ${promoted} text(s), ${failed.length} failed`,
      description: `${failed[0].Code}: ${failed[0].Error ?? "unknown error"}`,
    })
  }

  async function promotePlain() {
    try {
      const res = await promote({
        variables: { codes, withAiTranslations: false },
      })
      report(res.data?.promoteCodeTexts ?? [])
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Promote failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
    onComplete?.()
  }

  async function promoteWithAi() {
    const results: PromoteResult[] = []
    setAiProgress({ done: 0, total: codes.length })
    try {
      // One code per request: each is one DB-side LLM round-trip (seconds),
      // and per-request granularity gives resumable progress — a failure
      // mid-run leaves everything already promoted in place.
      for (let i = 0; i < codes.length; i++) {
        const isLast = i === codes.length - 1
        const res = await promote({
          variables: { codes: [codes[i]], withAiTranslations: true },
          // Refetch the (large) missing/catalog queries once, not per code.
          ...(isLast ? {} : { refetchQueries: [] }),
        })
        results.push(...(res.data?.promoteCodeTexts ?? []))
        setAiProgress({ done: i + 1, total: codes.length })
      }
      report(results)
    } catch (error) {
      toast({
        variant: "destructive",
        title: `AI promote stopped after ${results.length} of ${codes.length}`,
        description: error instanceof Error ? error.message : String(error),
      })
    }
    setAiProgress(null)
    onComplete?.()
  }

  return (
    <>
      <ConfirmButton
        variant="primary"
        isStacked
        iconSrc="arrow-up"
        disabled={busy || codes.length === 0}
        isLoading={promoteState.loading && aiProgress === null}
        content="Create a database record for each selected text, seeded with its English code text. They become editable under All texts and queue under Missing translations."
        confirmContent={`Promote ${codes.length} text(s) into the translation database?`}
        onConfirm={promotePlain}
        data-feature-code="UI_BackofficeTranslation_Missing_Promote"
      >
        Promote
      </ConfirmButton>

      <ConfirmButton
        variant="secondary"
        isStacked
        iconSrc="magic-wand"
        disabled={busy || codes.length === 0}
        isLoading={aiProgress !== null}
        content="Promote and let the AI draft Name + Description in every supported language per text (marked auto-translated for later human review). One AI call per text — spends tokens and takes a few seconds each."
        confirmContent={`Promote ${codes.length} text(s) and AI-draft all languages? Roughly a few seconds per text.`}
        onConfirm={promoteWithAi}
        data-feature-code="UI_BackofficeTranslation_Missing_PromoteAi"
      >
        {aiProgress
          ? `Translating ${aiProgress.done}/${aiProgress.total}…`
          : "Promote + AI translate"}
      </ConfirmButton>
    </>
  )
}

export default TranslationPromoteAction
